import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';

// material-ui
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import AnimateButton from 'components/@extended/AnimateButton';
import AuthWrapper from 'sections/auth/AuthWrapper';

// ============================|| DO-RA - ROUTE ERROR ||============================ //
// React Router's default error screen (raw stack trace, English, unstyled)
// shows whenever a route/lazy chunk throws - most commonly "Failed to fetch
// dynamically imported module" right after a deploy: the browser already
// has an old index.html (or WEDOS's edge CDN served a stale one for a few
// minutes - see .htaccess's index.html no-cache comment) pointing at a
// bundle hash that's since moved on. User-reported bug, 2026-08-21 -
// styled like the auth screens (AuthWrapper) instead of the raw dump, in
// Czech, with an automatic one-shot reload for exactly that chunk-error
// case (fixes itself without the user having to understand what happened),
// and a manual "Načíst znovu" fallback for anything else or a repeat.
//
// The auto-reload guard is a plain sessionStorage flag, never cleared here
// on purpose - at most one silent auto-reload per tab session, so a
// genuinely broken deploy doesn't reload-loop the visitor forever.
const AUTO_RELOAD_KEY = 'do-ra-route-error-auto-reload';

function isChunkLoadError(error) {
  const message = String(error?.message || error || '');
  return /dynamically imported module|Importing a module script failed/i.test(message);
}

export default function RouteError() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  const [autoReloading, setAutoReloading] = useState(false);

  useEffect(() => {
    if (chunkError && sessionStorage.getItem(AUTO_RELOAD_KEY) !== '1') {
      sessionStorage.setItem(AUTO_RELOAD_KEY, '1');
      setAutoReloading(true);
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (autoReloading) {
    return (
      <AuthWrapper>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Stack sx={{ gap: 2, alignItems: 'center', py: 2 }}>
              <CircularProgress size={28} />
              <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Aplikace byla mezitím aktualizována, načítám novou verzi...
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant="h3">Něco se pokazilo</Typography>
        </Grid>
        <Grid size={12}>
          <Alert severity="error">
            {chunkError
              ? 'Aplikace byla mezitím aktualizována a tuto stránku se nepodařilo načíst automaticky. Načtěte prosím stránku znovu.'
              : 'Došlo k neočekávané chybě. Zkuste prosím stránku načíst znovu.'}
          </Alert>
        </Grid>
        <Grid size={12}>
          <AnimateButton>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
              sx={{ textTransform: 'none' }}
            >
              Načíst znovu
            </Button>
          </AnimateButton>
        </Grid>
      </Grid>
    </AuthWrapper>
  );
}
